import React from "react";
import { Badge, Sheet, SheetContent, SheetHeader, SheetTitle, Typography } from "@altimateai/lego";
import { Teammate } from "@/pages/mcp/mcpSlice";
import useIntegrations from "@/pages/teammate/useIntegrations";
import ViewConnections from "@/pages/mcp/ViewConnections";
import { Stack } from "@altimateai/lego";
import { updateContext, useChatContext } from "@altimateai/ui-components/chatbotV2";
import { useConnections } from "@/hooks/useConnections";
import { getIntegrationsWithoutValidConfig } from "@/helpers/datamateValidation";

interface DatamateSettingsProps {
  datamate: Teammate;
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

const DatamateSettings: React.FC<DatamateSettingsProps> = ({ open, onOpenChange, datamate }) => {
  const { allIntegrations, isCustomIntegrationsLoading, isIntegrationsFromServerLoading } =
    useIntegrations();
  const { dispatch, currentSessionId, sessions } = useChatContext();

  const currentSession = currentSessionId ? sessions[currentSessionId] : null;
  const selectedConnectionsInSession = (currentSession?.context?.connections || {}) as Record<
    string,
    string
  >;

  // Use connections hook with datamate integrations
  const {
    connections,
    selectedConnections,
    handleConnectionSelect: hookHandleConnectionSelect,
    isLoading: isConnectionsLoading,
  } = useConnections({
    integrations: datamate?.integrations?.map(int => int.id) || [],
  });

  const isLoading =
    isCustomIntegrationsLoading || isIntegrationsFromServerLoading || isConnectionsLoading;

  // Custom handler that updates both hook state and context
  const handleConnectionSelect = (connectionId: string, integrationId: string) => {
    // Update hook state
    hookHandleConnectionSelect(connectionId, integrationId);

    // Update context immediately
    if (currentSessionId) {
      const updatedConnections = {
        ...selectedConnections,
        [integrationId]: connectionId,
      };
      dispatch(
        updateContext({
          sessionId: currentSessionId,
          context: { connections: updatedConnections },
        })
      );
    }
  };

  // Get integration data for datamate's integrations
  const datamateIntegrationsWithData =
    datamate.integrations
      ?.map(datamateIntegration => {
        const integrationData = allIntegrations.find(
          integration => integration.id === datamateIntegration.id
        );
        return integrationData;
      })
      .filter(Boolean) || [];

  // Use utility function for validation
  const invalidIntegrations = isLoading
    ? []
    : getIntegrationsWithoutValidConfig(datamate, allIntegrations, connections);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent onClick={e => e.stopPropagation()}>
        <SheetHeader>
          <SheetTitle>
            <Typography variant="h4">{datamate.name}</Typography>
          </SheetTitle>
        </SheetHeader>
        <div className="al-mt-4">
          {isLoading ? (
            <p className="al-text-sm al-text-muted-foreground">Loading integrations...</p>
          ) : (
            <div className="al-space-y-4" aria-label="Datamate integrations">
              <Typography variant="section-heading">Integrations</Typography>
              {datamateIntegrationsWithData.length === 0 ? (
                <p className="al-text-sm al-text-muted-foreground">No integrations configured</p>
              ) : (
                datamateIntegrationsWithData.map(integration => {
                  const isInvalid = invalidIntegrations.some(inv => inv?.id === integration?.id);
                  return (
                    <div
                      key={integration?.id}
                      className="al-border al-rounded-lg al-p-4"
                      aria-label={`${integration?.name} container`}
                    >
                      <Stack direction="row" justify="between" align="center" className="al-mb-2">
                        <Typography variant="body">{integration?.name}</Typography>
                        <Stack direction="row" gap="sm" align="center">
                          {isInvalid && (
                            <Badge variant="destructive" className="al-text-xs">
                              Missing Config
                            </Badge>
                          )}
                          <Badge variant="outline">{integration?.type}</Badge>
                        </Stack>
                      </Stack>

                      <ViewConnections
                        integration={integration!}
                        onSelect={handleConnectionSelect}
                        selectedConnectionId={selectedConnectionsInSession[integration?.id || ""]}
                      />
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default DatamateSettings;
